import { message, Table, InputNumber } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { useSelector } from "react-redux";
import "./task-resources.css";

// Типы ресурсов согласно API
const taskResTypes = [
  { value: 1, label: "Количество заказов" },
  { value: 2, label: "Количество изделий" },
  { value: 3, label: "Площадь изделий" },
  { value: 4, label: "Длина внутренних откосов" },
  { value: 5, label: "Длина внешних откосов" },
  { value: 6, label: "Общая длина откосов" },
];

// const taskResTypeRender = (value) => taskResTypes.find(i => i.value === value)?.label || value;

// Задачи с кодами 1..5
const TASKS = [
  { id: 1, name: "Замеры" },
  { id: 2, name: "Монтажи" },
  { id: 3, name: "Установка откосов" },
  { id: 4, name: "Рекламации" },
  { id: 5, name: "Производство" },
];

// Маппинг задачи -> типы ресурсов
const taskToResTypes = {
  1: [1], // Замеры: Количество заказов
  2: [2, 3], // Монтажи: Количество изделий, Площадь изделий
  3: [6], // Установка откосов: Общая длина откосов
  4: [1], // Рекламации: Количество заказов
  5: [2, 3], // Производство: Количество изделий, Площадь изделий
};

// Получить типы ресурсов для задачи
const getResTypesForTask = (taskId) => {
  return taskToResTypes[taskId] || [];
};

const daysOfWeek = [
  { key: 'qty1', label: 'Пн', dayIndex: 0 },
  { key: 'qty2', label: 'Вт', dayIndex: 1 },
  { key: 'qty3', label: 'Ср', dayIndex: 2 },
  { key: 'qty4', label: 'Чт', dayIndex: 3 },
  { key: 'qty5', label: 'Пт', dayIndex: 4 },
  { key: 'qty6', label: 'Сб', dayIndex: 5 },
  { key: 'qty7', label: 'Вс', dayIndex: 6 },
];

const TaskResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { taskId, resType, dayKey }
  const [editingValue, setEditingValue] = useState(null);
  const user = useSelector((s) => s.user);

  const getResources = () => {
    setLoading(true);
    serverFetch(`CompanyTask/Resources`)
      .then(data => {
        // 1. Заполни значениями null для всех комбинаций ресурсов по задачам (taskToResTypes), задачи 1..5
        const result = [];
        [1, 2, 3, 4, 5].forEach(taskId => {
          getResTypesForTask(taskId).forEach(resType => {
            result.push({
              companyId: user?.companyId,
              taskId,
              resType,
              qty1: 0,
              qty2: 0,
              qty3: 0,
              qty4: 0,
              qty5: 0,
              qty6: 0,
              qty7: 0,
            });
          });
        });
        // 2. Перебери все полученные от сервера записи, найди строку в result и установи количества
        (data || []).forEach(serverRecord => {
          const row = result.find(r => r.taskId === serverRecord.taskId && r.resType === serverRecord.resType);
          if (row) {
            row.qty1 = serverRecord.qty1 ?? 0;
            row.qty2 = serverRecord.qty2 ?? 0;
            row.qty3 = serverRecord.qty3 ?? 0;
            row.qty4 = serverRecord.qty4 ?? 0;
            row.qty5 = serverRecord.qty5 ?? 0;
            row.qty6 = serverRecord.qty6 ?? 0;
            row.qty7 = serverRecord.qty7 ?? 0;
            if (serverRecord.id != null) row.id = serverRecord.id;
          }
        });
        setResources(result);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    getResources();
  }, []);

  const handleCellChange = (taskId, resType, dayKey, value) => {
    const allowedResTypes = getResTypesForTask(taskId);
    if (!allowedResTypes.includes(resType)) {
      message.warning("Этот тип ресурса не доступен для данной задачи");
      setEditingCell(null);
      setEditingValue(null);
      return;
    }

    const resource = resources.find(r => r.taskId === taskId && r.resType === resType);
    const newValue = value === null || value === '' ? null : Math.round(Number(value));
    const updatedResource = {
      ...resource,
      [dayKey]: newValue,
    };
    const bodyData = {
      qty1: updatedResource.qty1,
      qty2: updatedResource.qty2,
      qty3: updatedResource.qty3,
      qty4: updatedResource.qty4,
      qty5: updatedResource.qty5,
      qty6: updatedResource.qty6,
      qty7: updatedResource.qty7,
    };

    setLoading(true);
    serverFetch(`CompanyTask/${taskId}/Resources/${resType}`, {
      method: 'PUT',
      bodyData,
    })
      .then(() => {
        setResources(prev => prev.map(r =>
          r.taskId === taskId && r.resType === resType ? updatedResource : r
        ));
        setLoading(false);
        setEditingCell(null);
        setEditingValue(null);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };



  // Создаем плоский список строк: одна строка на задачу + ресурсы
  const tableData = [];
  TASKS.forEach(task => {
    // Строка с задачей
    tableData.push({
      key: `task_${task.id}`,
      type: 'task',
      task: task,
    });
    
    // Строки с ресурсами (всегда показываем)
    const allowedResTypes = getResTypesForTask(task.id);
    allowedResTypes.forEach(resTypeValue => {
      const resType = taskResTypes.find(r => r.value === resTypeValue);
      if (resType) {
        const resource = resources.find(r => r.taskId === task.id && r.resType === resType.value);
        tableData.push({
          key: `resource_${task.id}_${resType.value}`,
          type: 'resource',
          task: task,
          resType: resType.value,
          resTypeLabel: resType.label,
          resource: resource,
        });
      }
    });
  });


  // Создаем колонки для таблицы
  const columns = [
    {
      title: 'Ресурс',
      dataIndex: 'resource',
      width: 250,
      render: (_, record) => {
        if (record.type === 'task') {
          return <strong>{record.task.name}</strong>;
        }
        return record.resTypeLabel;
      },
    },
    ...daysOfWeek.map(day => ({
      title: day.label,
      dataIndex: day.key,
      width: 100,
      align: 'center',
      render: (_, record) => {
        if (record.type === 'task') {
          return null;
        }
        
        const isEditing = editingCell?.taskId === record.task.id && 
                          editingCell?.resType === record.resType && 
                          editingCell?.dayKey === day.key;
        const value = record.resource?.[day.key];
        const displayValue = isEditing && editingValue !== null ? editingValue : value;
        
        return (
          <div className={`task-resources-day-cell ${day.dayIndex >= 5 ? 'weekend' : ''}`}>
            {isEditing ? (
              <InputNumber
                size="small"
                value={displayValue}
                min={0}
                step={1}
                precision={0}
                className="task-resources-input-number"
                autoFocus
                onChange={(newValue) => {
                  setEditingValue(newValue);
                }}
                onBlur={() => {
                  handleCellChange(record.task.id, record.resType, day.key, editingValue);
                  setEditingCell(null);
                  setEditingValue(null);
                }}
                onPressEnter={() => {
                  handleCellChange(record.task.id, record.resType, day.key, editingValue);
                  setEditingCell(null);
                  setEditingValue(null);
                }}
              />
            ) : (
              <div
                className="task-resources-editable-cell"
                onClick={() => {
                  setEditingCell({ taskId: record.task.id, resType: record.resType, dayKey: day.key });
                  setEditingValue(value);
                }}
                title="Нажмите для редактирования"
              >
                {value != null ? String(Math.round(Number(value))) : ''}
              </div>
            )}
          </div>
        );
      },
    })),
  ];

  return <div className="task-resources-container">
    <Table
      key={loading ? "loading" : "loaded"}
      dataSource={tableData}
      loading={loading}
      columns={columns}
      pagination={false}
      size="small"
      rowKey="key"
      components={{
        body: {
          cell: (props) => (
            <td {...props} className="task-resources-table-cell" />
          ),
        },
      }}
    />
  </div>
}

export default TaskResources;
